import { POST } from "../../API/API";
import { getBaseCode } from "../languageHelper";

const DEFAULT_BACKOFF_BASE_MS = 500;
const DEFAULT_BACKOFF_MAX_MS = 10_000;

const calculateAge = (birthdateString) => {
  if (!birthdateString) return null;
  const birthYear = new Date(birthdateString).getFullYear();
  const currentYear = new Date().getFullYear();
  const age = currentYear - birthYear;
  return age > 0 ? String(age) : null; 
};

const buildDictationConfiguration = ({ targetLanguage, ttsEnabled, speed, user }) => {
  const userLangCode = getBaseCode(user?.call_language || user?.language || "en"); 
  const targetLangCode = getBaseCode(targetLanguage);
  
  const dynamicGender = user?.gender || "neutral";
  const dynamicBornIn = user?.birthdate || "1990-01-01";
  const dynamicAge = calculateAge(user?.birthdate) || "30"; 
  const dynamicCulture = user?.cultural_background || "Unknown";
  const defaultMood = user?.default_mood || "neutral";

  const config = {
    translations_enabled: true,
    stt_enabled: true, 
    tts_enabled: Boolean(ttsEnabled),
    tts_speed: speed !== undefined ? speed : 1.0,
    tts_voice_gender: dynamicGender,
    language: userLangCode, 
    mood: defaultMood, 
    from: {
      language: userLangCode, 
      gender: dynamicGender,
      age: String(dynamicAge),
      born_in: dynamicBornIn,
      cultural_background: dynamicCulture
    },
    to: {
      language: targetLangCode,
      gender: dynamicGender, 
      age: String(dynamicAge),
      born_in: dynamicBornIn,
      cultural_background: dynamicCulture
    }
  };

  if (user?.voice_id) {
    config.tts_voice_id = user.voice_id;
  }

  return config;
};

const createDictationSession = async (configParams) => {
  const configuration = buildDictationConfiguration(configParams);
  
  return new Promise((resolve, reject) => {
    POST(
      "/api/v1/chat/live_feed/realtime_session",
      { configuration },
      (response) => resolve(response?.data),
      (error) => reject(error)
    );
  });
};

export default class RealtimeDictationClient {
  constructor({
    onOpen,
    onClose,
    onError,
    onTranslatedText,
    onRecognizedText,
    onAudio,
  } = {}) {
    this.ws = null;
    this.isConnecting = false;

    this.onOpen = onOpen;
    this.onClose = onClose;
    this.onError = onError;
    this.onTranslatedText = onTranslatedText;
    this.onRecognizedText = onRecognizedText;
    this.onAudio = onAudio;

    this.backoffAttempt = 0;
    this.reconnectTimer = null;

    this.targetLanguage = null;
    this.desiredTtsEnabled = false;
    this.speed = 1.0;
    this.user = {}; 
  }

  get isConnected() {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  async connect({ targetLanguage, ttsEnabled, speed, user } = {}) {
    if (!targetLanguage) throw new Error("targetLanguage is required for Dictation.");
    
    this.targetLanguage = targetLanguage;
    this.desiredTtsEnabled = Boolean(ttsEnabled);
    if (speed !== undefined) this.speed = speed;
    if (user) this.user = user;

    if (this.isConnecting || this.isConnected) return;
    this.isConnecting = true;
    
    try {
      const session = await createDictationSession({
        targetLanguage: this.targetLanguage,
        ttsEnabled: this.desiredTtsEnabled,
        speed: this.speed,
        user: this.user 
      });
      
      const accessUrl = session?.access_url;
      if (!accessUrl) throw new Error("Missing access_url from backend");
      await this.connectWebSocket(accessUrl);
    } catch (error) {
      this.isConnecting = false;
      if (this.onError) this.onError(error);
      throw error;
    } finally {
      this.isConnecting = false;
    }
  }

  async reconnect() {
    await this.disconnect();
    return this.connect({
      targetLanguage: this.targetLanguage,
      ttsEnabled: this.desiredTtsEnabled,
      speed: this.speed,
      user: this.user
    });
  }

  scheduleReconnect({ baseMs = DEFAULT_BACKOFF_BASE_MS, maxMs = DEFAULT_BACKOFF_MAX_MS } = {}) {
    if (!this.targetLanguage) return;
    this.clearReconnectTimer();
    const delay = Math.min(maxMs, baseMs * 2 ** this.backoffAttempt);
    this.backoffAttempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnect().catch((err) => {
        if (this.onError) this.onError(err);
        this.scheduleReconnect({ baseMs, maxMs });
      });
    }, delay);
  }

  clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  async disconnect() {
    this.clearReconnectTimer();
    if (this.ws) {
      try {
        this.ws.onopen = null;
        this.ws.onclose = null;
        this.ws.onerror = null;
        this.ws.onmessage = null;
        this.ws.close();
      } catch {}
      this.ws = null;
    }
    this.isConnecting = false;
  }

  async connectWebSocket(accessUrl) {
    await this.disconnect();
    return new Promise((resolve, reject) => {
      try {
        const ws = new WebSocket(accessUrl);
        ws.binaryType = "arraybuffer";
        this.ws = ws;
        
        ws.onopen = () => { if (this.onOpen) this.onOpen(); resolve(); };
        
        ws.onerror = (event) => {
          const err = event?.error || new Error("Websocket connection error");
          if (this.onError) this.onError(err);
          reject(err);
        };
        
        ws.onclose = () => { if (this.onClose) this.onClose(); };
        
        ws.onmessage = (event) => {
          const data = event?.data;
          if (typeof data === "string") { this.handleJsonMessage(data); return; }
          if (data instanceof ArrayBuffer) { if (this.onAudio) this.onAudio(data); return; }
          if (data instanceof Blob) {
            data.arrayBuffer().then((buf) => { if (this.onAudio) this.onAudio(buf); }).catch(() => {});
          }
        };
      } catch (err) { reject(err); }
    });
  }

  handleJsonMessage(raw) {
    let parsed;
    try { parsed = JSON.parse(raw); } catch { return; }
    if (parsed?.type === "text_translated" && this.onTranslatedText) { this.onTranslatedText(parsed); return; }
    if (parsed?.type === "text_recognized" && this.onRecognizedText) { this.onRecognizedText(parsed); return; }
    if (parsed?.type === "error" && this.onError) { this.onError(new Error(parsed?.message || parsed?.code || "API error")); }
  }

  sendJson(payload) {
    if (!this.isConnected) throw new Error("Websocket not connected");
    this.ws.send(JSON.stringify(payload));
  }

  sendAudio(arrayBuffer) {
    if (!arrayBuffer || arrayBuffer.byteLength === 0 || !this.isConnected) return;
    this.ws.send(arrayBuffer);
  }

  async updateConfiguration(configParams = {}) {
    if (!this.isConnected) return;
    
    if (configParams.targetLanguage) this.targetLanguage = configParams.targetLanguage;
    if (configParams.ttsEnabled !== undefined) this.desiredTtsEnabled = Boolean(configParams.ttsEnabled);
    if (configParams.speed !== undefined) this.speed = configParams.speed;

    const configuration = buildDictationConfiguration({
      targetLanguage: this.targetLanguage,
      ttsEnabled: this.desiredTtsEnabled,
      speed: this.speed,
      user: this.user
    });

    this.sendJson({ type: "update_configuration", configuration });
  }
}